/**
 * Properties menu: clickable list of B2B, User or Event properties and cohorts to go inside mp-drop-menu
 */
import compact from 'lodash/compact';
import cuid from 'cuid';
import debounce from 'lodash/debounce';
import flatten from 'lodash/flatten';
import intersectionBy from 'lodash/intersectionBy';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';

import {
  DISABLED_DUE_TO_CLASSIFIED_DATA,
  ProjectFeatureFlag,
  SPECIAL_EVENTS,
  STRING_TYPECAST_PROPERTIES,
  TYPECAST_PROPERTY_TYPES,
} from 'common/report/constants';
import {eventPropertyDefinitionsStore, peoplePropertyDefinitionsStore} from 'common/report/stores/property-definitions';
import {userDetailsClient} from 'common/report/queries/app-api/user-details-client';
import {userDetailsStore} from 'common/report/stores/user-details';
import {dataGroupIDsEqual} from 'common/util';
import {Cohort as CohortModel} from 'common/widgets/cohorts-query-builder/models/cohort';
import {
  isSerializedInlineCohort,
  SerializedCohort,
  SerializedInlineCohort,
  User,
} from 'common/widgets/cohorts-query-builder/models/types/serialized-cohort-types';
import {promptCustomProperties} from 'common/widgets/custom-property-modal';
import {
  OrderedTimeUnit,
  SpecialTimeUnit,
  TimeUnit,
  TimeUnitLabels,
} from 'common/widgets/date-range-picker/models/types';
import {promptInlineCohorts} from 'common/widgets/inline-cohorts-modal/util';
import {INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING} from 'common/widgets/property-filter-menu/models/insights-filter';
import {eventPropertiesClient} from 'common/report/queries/query-api/event-properties-client';
import {peoplePropertiesClient} from 'common/report/queries/query-api/people-properties-client';
import {cohortsStore} from 'common/report/stores/cohorts';
import {customPropertiesStore, migrateBehavioralPropertyDefinition} from 'common/report/stores/custom-properties';
import {eventPropertiesStore} from 'common/report/stores/event-properties';
import {peoplePropertiesStore} from 'common/report/stores/people-properties';
import {currentProjectStore} from 'common/report/stores/project-details';
import {Store} from 'common/report/stores/store';
import {getIconForPropertyType, getLabelForPropertyType} from 'common/report/util/properties';
import {Cohort, MpProperty, PropertyDefinition} from 'common/util/definitions-types';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {
  getInlineCustomPropertyId,
  isBehavioralCustomProperty,
  isPersistedCustomProperty,
} from 'common/widgets/custom-property-modal/util';
import {DataGroup} from 'common/widgets/data-group-selector/types';
import {PropertyType, ResourceType} from 'common/widgets/property-filter-menu/models/types';
import {promptSharing} from 'common/widgets/sharing';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {getTooltipDataForCohort, getTooltipDataForProperty} from 'common/widgets/context-panel/util';
import {SavedCohortItem} from 'common/widgets/query-entry-section/types';
import {EntityType, SharableEntityType} from 'common/report/util/saved-entities/types';
import {promptSuperadminOverrideShare} from 'common/report/util/saved-entities';
import {featuresStore} from 'common/report/stores/features';
import {
  eventPrefix as shareEventPrefix,
  ShareButtonUISource,
  track as shareTrack,
} from 'common/widgets/sharing/tracking';
import {
  fetchLookupTableProperties,
  getTempLookupTableMappings,
  lookupTablesStore,
  registerTempLookupTableMapping,
} from 'common/report/stores/lookup-tables';
import {SearchMatchResult, Section} from 'common/components/items-menu/types';
import {InlineCohortsModalCommitEvent} from 'common/widgets/inline-cohorts-modal/types';

import {ChildMenu, TooltipContextActionResult} from '../child-menu';
import {
  calculatePropertiesIntersection,
  createHiddenMatchItem,
  CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING,
  getDataDefinitionsPropertyMenuItem,
  sanitizeCustomProperty,
  sortPropertyItems,
  storeCustomPropertyToProperty,
} from './util';
import {TrackingEvent} from './tracking';
import {
  CohortItem,
  GetSortedMenuSections,
  IPropertiesMenu,
  PropertiesMenuSection,
  PropertiesMenuSectionType,
  PropertyCombineMode,
  PropertyItem,
  PropertyItemAction,
  PropertyItemKeys,
  PropertyItemPersistedIdentifierKeys,
  SelectedTab,
  StagedMenuTypes,
} from './types';
import {
  ALL_USERS_COHORT,
  DATASET_PROPERTY,
  DATE_PROPERTY,
  FIRST_EVENT_TIME_PROPERTY,
  OTHER_OPTIONS,
  SPECIAL_CONTROLS,
  SPECIAL_PROPERTIES,
  TOP_PEOPLE,
} from './constants';
import template from './index.jade';
import css from './index.styl?inline';

// number of items to show per section on the "All" tab
// remaining items are hidden under a `Show all` expander
const MENU_ITEM_LIMIT = 10;

const PropertyDefinitionStoreMap = {
  [ResourceType.User]: peoplePropertyDefinitionsStore,
  [ResourceType.Event]: eventPropertyDefinitionsStore,
};

const DefaultToggleTab = `all`;

enum EventPropertySource {
  Empty,
  Intersection,
  Store,
}

enum MODAL_TYPE {
  InlineCohort = `inline_cohort`,
  CustomProperty = `custom_property`,
  BehavioralCustomProperty = `behavioral_custom_property`,
}

type DataGroupItem = DataGroup & {
  properties: any[];
  isLoading: boolean;
};

enum IncludeCustomPropertyType {
  All = `all`,
  None = `none`,
  PersistedOnly = `persisted-only`,
  PersistedNonBehavioralOnly = `persisted-non-behavioral-only`,
}

interface PropertiesMenuState {
  // used specifically to remove context panel from showing unexpectedly for custom props and cohorts
  modalShowing: boolean;
  dataGroups: {[id: string]: DataGroupItem};
  eventProperties: any[];
  featureFlags: Set<string>;
  permissionList: any;
  lastMultiSelection: PropertyItem[];
  loadingCohorts: boolean;
  loadingEventProperties: boolean;
  loadingPeopleProperties: boolean;
  selectedTab: SelectedTab;
  stagedProperty: PropertyItem;
  stagedMenuType: StagedMenuTypes | null;
  tooltipData: ContextPanelData;
  stagedSearchFilter: string;
  getSortedMenuSections: GetSortedMenuSections;
  includeHiddenEventProps: boolean;
  includeHiddenUserProps: boolean;
}

export class PropertiesMenu extends ChildMenu<PropertiesMenuState> implements IPropertiesMenu {
  private _eventPropertySource;
  private _useMultipleSelection = false;
  private _context = null;
  private _cohortStoreCallback = null;
  private _customPropertiesStoreCallback = null;
  private _eventPropertiesStoreCallback = null;
  private _eventPropertiesStoreKey: string = null;
  private _peoplePropertiesStoreCallback = null;
  private _currentProjectStoreCallback = null;
  private _lookupTablesStoreCallback = null;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'cohort-operation-type': `string`,
      'apply-button': {
        type: `string`,
        default: null,
      },
      'apply-button-disabled': `boolean`,
      'allow-typecast': {
        type: `boolean`,
        description: `allows typecasting for property selection`,
      },
      'enable-workspace-filter-restrictions': `boolean`,
      'disabled-items': {
        type: `json`,
        default: {}, // dict for fast inclusion lookups
        description: `sets disabled options for the underlying mp-items-menu`,
      },
      'include-custom-properties': {
        type: `string`,
        enum: Object.values(IncludeCustomPropertyType),
        default: IncludeCustomPropertyType.None,
        description: `whether to hide custom properties from properties menus`,
      },
      'exclude-dimension-properties': {
        type: `boolean`,
        description: `whether to hide dimension properties from properties menus`,
      },
      'property-combine-mode': {
        type: `string`,
        default: PropertyCombineMode.Union,
        description: `defines how event properties should be merged`,
        enum: Object.values(PropertyCombineMode),
      },
      'show-all-tab': {
        type: `boolean`,
        description: `enables the "All" tab in the selection menu when there is more than one section`,
      },
      'show-all-users-cohort': {
        type: `boolean`,
        description: `enables the "All Users" cohort if cohort selection is enabled`,
      },
      'show-items-caret': {
        type: `boolean`,
        description: `enables the a caret icon in the selection items`,
      },
      'single-data-group': {
        type: `string`,
        description: `if set, shows group properties for a specific data group id`,
      },
      'other-options': {
        type: `json`,
        default: [],
        description: `list of items to show in the other section`,
      },
      'show-select-all': `boolean`,
      'select-all-disabled': `boolean`,
      'hide-recently-viewed': `boolean`,
      'hide-section-labels': `boolean`,
      'selectable-sensitive-properties': `boolean`,
      'description-edit-disabled': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        modalShowing: false,
        dataGroups: {},
        eventProperties: [],
        featureFlags: null,
        permissionList: [],
        isLoading: false,
        loadingCohorts: false,
        loadingEventProperties: false,
        loadingPeopleProperties: false,
        selectedTab: DefaultToggleTab,
        stagedProperty: null,
        stagedMenuType: null,
        lastMultiSelection: null,
        tooltipData: null,
        stagedSearchFilter: ``,
        getSortedMenuSections: null,
        includeHiddenEventProps: false,
        includeHiddenUserProps: false,
      },

      helpers: {
        ...super.config.helpers,
        StagedMenuTypes,
        getMenuSections: () => this.getMenuSections(),
        getToggleTabs: () => this.getToggleTabs(),
        onItemClick: (ev) =>
          this.onItemClick(
            ev.detail.item,
            ev.detail.searchFilter,
            ev.detail.labelMatch,
            ev.detail.subLabelMatch,
            ev.detail.customMatch,
          ),
        onItemPillClick: (ev) => this.handleItemPillClick(ev.detail.item),
        onAllSelected: ({detail}) => this.dispatchEvent(new CustomEvent(`selectAll`, {detail})),
        applyStagedValue: (ev) => this.applyStagedValue(ev.detail.item),
        unstageProperty: () => this.updateStagedProperty(null, null),
        showItemDetails: debounce((ev) => this.showItemDetails(ev.detail.item), 100),
        useMultipleSelection: () => this.isMultipleSelection(),
        isInnerContentSearchable: () => this.isInnerContentSearchable(),
        getSearchFilter: () => this.state.searchFilter || this.attr(`search-filter`),
        getDefaultFormulaName: () => {
          const entityNames = this.getCustomEntities([ResourceType.Event, ResourceType.User]).map((item) => item.value);
          const prefix = this.attr(`cohort-operation-type`)
            ? `Custom ${this.attr(`cohort-operation-type`)}`
            : `Custom formula`;
          for (let i = 1; ; i++) {
            const name = `${prefix} ${i}`;
            if (!entityNames.includes(name)) {
              return name;
            }
          }
        },

        handleTooltipContextEditDescription: (ev) => this.helpers.propagateEvent(ev),

        handleTooltipContextAction: (ev) => {
          const {name} = ev.detail;
          this.update({modalShowing: true});
          if ([`shareCohort`, `shareCustomProperty`].includes(name)) {
            this.handleShareTooltipContextAction(ev);
          } else {
            const result = this.handleTooltipContextAction(ev);
            if (!result || !result.handled) {
              this.helpers.propagateEvent(ev);
            }
          }
          this.helpers.closeModal();
        },

        searchInputInserted: (vnode) => {
          requestAnimationFrame(() => vnode.elm.focus());
        },

        handleSearchInput: (ev) => {
          ev.stopPropagation();
          this.update({stagedSearchFilter: ev.target.value});
        },

        selectOption: (ev) => {
          const {selected} = ev.detail;

          this.update({selectedTab: selected});
          this.trackEvent(TrackingEvent.SelectPropertiesDropdownOption, {
            context: this._context,
            selectedTab: selected,
          });
          this.dispatchEvent(new CustomEvent(`selectoption`, {detail: {selected}}));
        },

        getStagedMenuHeader: () => {
          const {stagedProperty, stagedMenuType} = this.state;

          switch (stagedMenuType) {
            case StagedMenuTypes.Typecast:
              return `${stagedProperty.label} Typecast`;
            case StagedMenuTypes.Datetime:
              return `Date Time Unit`;
            case StagedMenuTypes.Dimension:
              return `${stagedProperty.label}`;
            default:
              return null;
          }
        },

        getStagedMenuSections: () => {
          const {stagedMenuType} = this.state;

          switch (stagedMenuType) {
            case StagedMenuTypes.Typecast:
              return this.helpers.getTypecastMenuSections();
            case StagedMenuTypes.Datetime:
              return this.helpers.getDatetimeMenuSections();
            case StagedMenuTypes.Dimension:
              return this.helpers.getDimensionMenuSection();
          }
        },

        getDimensionMenuSection: () => {
          const {
            customProperty,
            customPropertyId,
            dimensionDataGroupId,
            tempDataGroupId,
            joinPropertyType,
            name,
            label,
            resourceType,
            value: dimension,
          } = this.state.stagedProperty;

          const dataGroupId = tempDataGroupId || dimensionDataGroupId;

          const properties = [
            // Include the dim prop underlying property.
            {
              name,
              label,
              dimensionDataGroupId: dataGroupId,
              type: joinPropertyType,
              value: dimension,
              customProperty,
              customPropertyId,
            },
            ...(lookupTablesStore.value[dataGroupId]?.properties || []).map((property) => ({
              ...property,
              dimensionDataGroupId: dataGroupId,
              value: [dimension, property.name],
            })),
          ];

          return [
            {
              items: this.getPropertyMenuItems({
                properties,
                resourceType,
              }),
            },
          ];
        },

        getDatetimeMenuSections: () => {
          const timeUnits = OrderedTimeUnit.filter((unit) => unit !== TimeUnit.Minute);
          const units = [...timeUnits, ...Object.values(SpecialTimeUnit)];

          const items = units.map((value) => {
            return {
              label: TimeUnitLabels[value],
              value,
            };
          });

          return [{items}];
        },

        getTypecastMenuSections: () => {
          const typeCastTypes = Object.values(TYPECAST_PROPERTY_TYPES);
          const {type} = this.state.stagedProperty;
          const types = [...typeCastTypes, ...(typeCastTypes.includes(type) ? [] : [type])];

          const items = types.map((typeCastType) => {
            return {
              icon: getIconForPropertyType(typeCastType),
              isSelected: typeCastType === type,
              label: getLabelForPropertyType(typeCastType),
              value: typeCastType,
            };
          });

          return [{items}];
        },

        sectionsFromInput: (input: string, resourceType, sections: Section[]) => {
          const items = [];

          const numHiddenEventProperties =
            sections.find(({label}) => label === `Hidden event properties`)?.items?.length || 0;
          const numHiddenUserProperties =
            sections.find(({label}) => label === `Hidden profile properties`)?.items?.length || 0;

          const {includeHiddenEventProps, includeHiddenUserProps} = this.state;
          switch (resourceType) {
            case `all`:
              if (!includeHiddenEventProps) {
                items.push(
                  createHiddenMatchItem(ResourceType.Event, numHiddenEventProperties, {includeResourceInLabel: true}),
                );
              }
              if (!includeHiddenUserProps) {
                items.push(
                  createHiddenMatchItem(ResourceType.User, numHiddenUserProperties, {includeResourceInLabel: true}),
                );
              }
              break;
            case ResourceType.Event:
              if (!includeHiddenEventProps) {
                items.push(createHiddenMatchItem(ResourceType.Event, numHiddenEventProperties));
              }
              items.push({
                type: PropertyType.String,
                propertyDefaultType: PropertyType.String,
                label: input,
                value: input,
                isSelected: this.isItemSelected(input, this.attr(`selected`)),
                resourceType,
                icon: `plus`,
              });
              break;
            case ResourceType.User:
              if (!includeHiddenUserProps) {
                items.push(createHiddenMatchItem(ResourceType.User, numHiddenUserProperties));
              }
              items.push({
                type: PropertyType.String,
                propertyDefaultType: PropertyType.String,
                label: input,
                value: input,
                isSelected: this.isItemSelected(input, this.attr(`selected`)),
                resourceType,
                icon: `plus`,
              });
              break;
            default:
              break;
          }

          return [{items: items.filter(Boolean)}];
        },

        //#region Custom properties
        closeModal: () => {
          this.update({
            modalShowing: false,
            tooltipData: null,
          });
        },
        deletedCustomProperty: () => {
          const selectedCustomPropertyId = (this.attr(`selected`) || {}).customPropertyId;
          // when filter menu is open, `selected` on the underlying
          // properties-menu is set to null, so we have to manually delete
          // ourselves here. the delete callback can handle anything else.
          if (!selectedCustomPropertyId) {
            this.dispatchEvent(new CustomEvent(`deletecustomproperty`));
          }
          this.helpers.closeModal();
        },
        applyCustomProperty: (customProp) => {
          let {resourceType, customProperty, customPropertyId} = customProp;
          const selectedCustomPropertyId = (this.attr(`selected`) || {}).customPropertyId;
          if (
            !selectedCustomPropertyId ||
            (selectedCustomPropertyId !== customPropertyId &&
              (isPersistedCustomProperty(selectedCustomPropertyId) ||
                customPropertiesStore.value[selectedCustomPropertyId].newCustomPropertyId !== customPropertyId))
          ) {
            // we only deal with the 'create' case and the 'update' case for the opened filter
            // menu since it doesn't have a `selected` set

            // we use 'user' and 'event' in the new query builder
            if (Object.keys(INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING).includes(resourceType)) {
              resourceType = INSIGHTS_PROPERTY_FILTER_RESOURCE_TYPE_MAPPING[resourceType];
            }
            customProperty = sanitizeCustomProperty(customProperty);
            if (isPersistedCustomProperty(customPropertyId)) {
              customPropertyId = Number(customPropertyId);
            }
            const property = {
              ...customProp,
              label: customProperty.name,
              customProperty,
              customPropertyId,
              resourceType,
            };
            this.selectItem(property, {rawItem: property});
          }
          this.helpers.closeModal();
        },
        onApplyButtonClick: () => {
          const selection = this.getArraySelection();
          if (!this.attr(`apply-button-disabled`)) {
            this.dispatchEvent(
              new CustomEvent(`select`, {
                detail: {
                  item: selection,
                  rawItem: null,
                  onItemClicked: false,
                  commit: true,
                },
              }),
            );
          }
        },
        //#endregion
      },
    };
  }

  shouldCloseOnSelect() {
    return !this.attr(`apply-button`);
  }

  customPropertiesStoreCallback() {
    const selectedCustomPropertyId = (this.attr(`selected`) || {}).customPropertyId;
    if (selectedCustomPropertyId in customPropertiesStore.value) {
      const newCustomPropertyId = customPropertiesStore.value[selectedCustomPropertyId].newCustomPropertyId;

      const property = storeCustomPropertyToProperty(newCustomPropertyId || selectedCustomPropertyId);

      const selectedCustomProperty = sanitizeCustomProperty((this.attr(`selected`) || {}).customProperty);

      if (newCustomPropertyId || !isEqual(property.customProperty, selectedCustomProperty)) {
        // update for breakdown menu and filter menus that are closed (the opened one has no `selected` set)
        const item = this.getPropertyMenuItem({
          property,
          resourceType: property.resourceType,
        });
        this.selectItem(item, {rawItem: item, onItemClicked: false, autoTriggered: true});
      }
    } else if (
      isPersistedCustomProperty(selectedCustomPropertyId) &&
      Object.keys(customPropertiesStore.value).length > 0
    ) {
      // delete
      this.dispatchEvent(new CustomEvent(`deletecustomproperty`));
    }
  }

  public async connectedCallback() {
    super.connectedCallback();

    this.fetchData();
    this.updateFromSelection();

    this._customPropertiesStoreCallback = () => this.customPropertiesStoreCallback();

    customPropertiesStore.subscribe(this._customPropertiesStoreCallback);
    await customPropertiesStore.fetch({
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });

    this.loadStoreValues();

    this._currentProjectStoreCallback = () => this.update();
    currentProjectStore.subscribe(this._currentProjectStoreCallback).fetch();
  }

  public disconnectedCallback() {
    customPropertiesStore.unsubscribe(this._customPropertiesStoreCallback);
    eventPropertiesStore.unsubscribe(this._eventPropertiesStoreCallback, this._eventPropertiesStoreKey);
    peoplePropertiesStore.unsubscribe(this._peoplePropertiesStoreCallback);
    lookupTablesStore.unsubscribe(this._lookupTablesStoreCallback);

    if (this.attr(`project-has-cohorts`)) {
      const dataGroupId = this.attr(`data-group-id`);
      if (dataGroupId && this.attr(`support-b2b-cohorts`)) {
        cohortsStore.unsubscribe(this._cohortStoreCallback, dataGroupId);
      } else {
        cohortsStore.unsubscribe(this._cohortStoreCallback);
      }
    }

    this._currentProjectStoreCallback && currentProjectStore.unsubscribe(this._currentProjectStoreCallback);
    this._currentProjectStoreCallback = null;

    super.disconnectedCallback();
  }

  handleCloseMenu() {
    this.helpers.showItemDetails.cancel();
  }

  public loadStoreValues() {
    // all properties-menus upload any inline custom properties they host.
    const inlineCustomProperties = flatten(
      this.getPropertyResourceTypes().map((type) => {
        return this.getSelectedCustomFormula(type);
      }),
    )
      .map((cur) => {
        if (cur.customProperty) {
          cur.customProperty = migrateBehavioralPropertyDefinition(cur.customProperty);
        }
        return cur;
      })
      .reduce((acc, cur) => {
        const {customPropertyId, customProperty} = cur;
        return {
          ...acc,
          [getInlineCustomPropertyId(customPropertyId)]: customProperty,
        };
      }, {});

    customPropertiesStore.update(inlineCustomProperties);

    // load all inline cohorts into cohort store
    cohortsStore.update(
      this.getInlineCohortItems().reduce((acc, cur: PropertyItem) => {
        return {
          ...acc,
          [cur.value.cohort.raw_cohort.unsavedId]: {
            name: cur.label,
            created_by: cur.createdBy,
            raw_cohort: cur.value.cohort.raw_cohort,
          } as SerializedInlineCohort,
        };
      }, {}),
    );
  }

  public isContentSearchable() {
    return !this.state.stagedProperty;
  }

  public isInnerContentSearchable() {
    return this.state.stagedProperty && this.state.stagedMenuType === StagedMenuTypes.Dimension;
  }

  /**
   * Sets the active tab to the section that the currently selected values belongs to.
   * If properties from multiple tabs are selected, sets the "All" tab when enabled, or to the first tab otherwise
   */
  public updateFromSelection() {
    let selected = this.attr(`selected`) || [];
    selected = Array.isArray(selected) ? selected : [selected];

    const resourceType = selected[0]?.resourceType;
    const dataGroupId = selected[0]?.dataGroupId;
    const isCohort = Array.isArray(this.attr(`selected`)) && (!resourceType || resourceType === ResourceType.Cohort);
    const isDimProp = this.isDimensionSelected(selected[0]);
    const isB2BTab = dataGroupId && !isDimProp;

    const selectedTab = (isCohort && ResourceType.Cohort) || (isB2BTab && dataGroupId) || resourceType;
    const tabs = this.getToggleTabs();

    if (selectedTab) {
      if (resourceType === ResourceType.Other) {
        this.update({selectedTab: DefaultToggleTab});
        return;
      }
      this.update({selectedTab});
    } else if (tabs.length && !tabs.find((tab) => tab.value === DefaultToggleTab)) {
      this.update({selectedTab: tabs[0].value});
    } else {
      this.update({selectedTab: DefaultToggleTab});
    }
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    // only update properties if project-id has already been defined
    if (!this.initialized || !this.attr(`project-id`)) {
      return;
    }

    switch (attr) {
      case `all-data-groups`:
      case `data-group-id`:
      case `event-names`:
      case `include-custom-properties`:
      case `project-has-cohorts`:
      case `hide-events`:
      case `project-has-people`:
      case `project-id`:
      case `property-combine-mode`:
      case `single-data-group`:
        this.fetchData();
        this.updateFromSelection();
        break;
      case `selected`:
        this.loadStoreValues();
        if (!this.attr(`open`)) {
          this.updateFromSelection();
        }
        break;
      case `open`:
        if (this.attr(`open`)) {
          // stages lastMultiSelection from initial selected state
          const selectedArray = this.getArraySelection();
          this.update({
            lastMultiSelection: Array.from(selectedArray),
            includeHiddenEventProps: false,
            includeHiddenUserProps: false,
          });

          const selected = this.attr(`selected`);
          if (this.isDimensionSelected(selected)) {
            // open dimension property submenu
            const property = this.getLookupTablePropertyFromSelected(selected);
            if (property) {
              const item = this.getPropertyMenuItem({
                property,
                resourceType: selected.resourceType,
              });
              this.updateStagedProperty(item, StagedMenuTypes.Dimension);
            }
          }
        } else {
          this.update({
            stagedProperty: null,
            tooltipData: null,
          });
        }
        break;
      default:
        break;
    }

    this.customPropertiesStoreCallback();
  }

  public getSelectedTab() {
    return this.state.selectedTab;
  }

  /**
   * Defines if the selection menu allows multiple selection, either by props or attributes
   */
  public isMultipleSelection() {
    return this._useMultipleSelection || this.attr(`multiple`);
  }

  public getStagedPropertyType() {
    return this.state.stagedMenuType;
  }

  // overriding ChildMenu::getSelectedPropertyName() is intentional, properties-menu uses Store objects to prefer canonical entity names
  public getSelectedPropertyName(): string {
    const selected = this.getSelectedProperty();
    const firstItem = Array.isArray(selected) ? selected[0] : selected;

    if (!firstItem) {
      return ``;
    }

    // use canonical cohort names when available
    if (firstItem.resourceType === ResourceType.Cohort && Number.isInteger(firstItem.value)) {
      return cohortsStore.value[firstItem.value]?.name || firstItem.label;
    }

    return super.getSelectedPropertyName();
  }

  public selectedLabel() {
    const selected = this.getSelectedProperty();
    let label;
    if (selected && selected.renamed) {
      label = `${selected.renamed} (${selected.label})`;
    } else {
      label = this.getSelectedPropertyName() || `Select a property`;
    }

    return label;
  }

  public selectedIcon() {
    let selected = this.attr(`selected`) || [];
    selected = Array.isArray(selected) ? selected[0] : selected;
    const resourceType = selected?.resourceType;

    switch (resourceType) {
      case ResourceType.User: {
        return selected.dataGroupId ? `group-attribute` : `profile-attribute`;
      }
      case ResourceType.Event:
        return `event-attribute`;
      case ResourceType.Cohort:
        return `community`;
      default: {
        // special prop icons
        if (selected?.value === TOP_PEOPLE.value) {
          return TOP_PEOPLE.icon;
        }
        return null;
      }
    }
  }

  public getItemUniqueKey(item) {
    const primaryKey = PropertyItemPersistedIdentifierKeys.find((key) => Boolean(item[key]));
    if (primaryKey === `dataGroupId`) {
      return item.dataGroupId + String(item.value);
    } else if (primaryKey === `tempDataGroupId`) {
      return `temp` + item.tempDataGroupId + String(item.value);
    } else {
      return item[primaryKey];
    }
  }

  public getPersistenceKey() {
    return `properties-menu`;
  }

  private async clickEditCustomProperty(property, modalType) {
    const {appliedValue} = await this.promptCustomPropertiesModal(property, modalType);
    if (appliedValue) {
      this.helpers.applyCustomProperty(appliedValue);
      this.trackEvent(TrackingEvent.EditCustomProperty, {
        context: this._context,
        name: property.name,
        resourceType: property.resourceType,
      });
    }
  }

  private handleItemPillClick(item) {
    if (item.pillAction === PropertyItemAction.Typecast) {
      this.updateStagedProperty(item, StagedMenuTypes.Typecast);
    } else {
      console.warn(`Invalid primary action for property item`);
    }
  }

  private updateStagedProperty(stagedProperty, stagedMenuType) {
    this.update({stagedProperty, stagedMenuType, stagedSearchFilter: ``});
    this.dispatchEvent(new CustomEvent(`stagedproperty`, {detail: {item: stagedProperty}}));
  }

  private async onItemClick(item, searchFilter?, labelMatch?, subLabelMatch?, customMatch?) {
    const rawItem = item;
    item = pick(item, PropertyItemKeys);
    // prevent the context menu from showing up after selecting a formula
    this.helpers.closeModal();

    if (rawItem.value === SPECIAL_CONTROLS.CREATE_INLINE_COHORT.value) {
      this.trackEvent(TrackingEvent.OpenInlineCohortsModal, {
        action: `Create`,
        context: this._context,
      });
      const {cohortStagedValue, cohortSavedValue} = await this.promptInlineCohortsModal(new CohortModel());
      this.helpers.closeModal();
      this.handleCohortUpdate(cohortStagedValue, cohortSavedValue);
    } else if (
      [
        SPECIAL_CONTROLS.CREATE_CUSTOM_EVENT_FORMULA.value,
        SPECIAL_CONTROLS.CREATE_CUSTOM_PEOPLE_FORMULA.value,
        SPECIAL_CONTROLS.CREATE_CUSTOM_BEHAVIORAL_PROPERTY.value,
      ].includes(rawItem.value)
    ) {
      const modalType =
        rawItem.value === SPECIAL_CONTROLS.CREATE_CUSTOM_BEHAVIORAL_PROPERTY.value
          ? MODAL_TYPE.BehavioralCustomProperty
          : MODAL_TYPE.CustomProperty;
      const props = pick(item, [`resourceType`]);
      const {appliedValue, deletedValue, didClose} = await this.promptCustomPropertiesModal(props, modalType);
      if (appliedValue) {
        this.helpers.applyCustomProperty(appliedValue);
      } else if (didClose) {
        this.helpers.closeModal();
      } else if (deletedValue) {
        this.helpers.deletedCustomProperty();
      }
      this.trackEvent(TrackingEvent.CreateCustomProperty, {
        context: this._context,
        name: item.name,
        ...props,
      });
    } else if (item?.value?.showHiddenResource) {
      this.trackEvent(TrackingEvent.ShowHiddenEntity);
      switch (item?.value?.showHiddenResource) {
        case ResourceType.Event:
          this.update({includeHiddenEventProps: true});
          break;
        case ResourceType.User:
          this.update({includeHiddenUserProps: true});
          break;
        default:
          break;
      }
    } else {
      this.selectItem(item, {rawItem, onItemClicked: true, searchFilter, labelMatch, subLabelMatch, customMatch});
    }
  }

  private handleCohortUpdate(cohortStagedValue, cohortSavedValue) {
    if (cohortStagedValue || cohortSavedValue) {
      this.commitCohort(cohortStagedValue ?? cohortSavedValue);
    }
    this.helpers.closeModal();
  }

  private promptCustomPropertiesModal(property, modalType) {
    const attrs = {
      'default-name': this.helpers.getDefaultFormulaName(),
      'behavioral-properties-mode': modalType === MODAL_TYPE.BehavioralCustomProperty,
      'enable-workspace-filter-restrictions': this.attr(`enable-workspace-filter-restrictions`),
      open: true,
      'project-id': this.attr(`project-id`),
      'user-id': this.attr(`user-id`),
      property: JSON.stringify(property),
      'track-name': this.attr(`track-name`),
      'workspace-id': this.attr(`workspace-id`),
    };
    this.update({modalShowing: true});
    return promptCustomProperties(attrs);
  }

  private promptInlineCohortsModal(cohort: CohortModel) {
    const attrs = {
      'can-save': this.attr(`project-has-cohorts`),
      'can-edit': this.attr(`project-has-cohorts`),
      'data-group-id': this.attr(`data-group-id`),
      elref: `inlineCohortsModal`,
      'is-new-cohort-definition': !cohort?.id,
      'can-apply-unsaved-cohort': !cohort?.id,
      name: cohort?.name,
      open: true,
      'project-has-cohorts': this.attr(`project-has-cohorts`),
      'project-id': this.attr(`project-id`),
      cohort: cohort?.serialize(),
      'track-name': this.attr(`track-name`),
      type: this.attr(`cohort-operation-type`),
      'user-id': this.attr(`user-id`),
      'workspace-id': this.attr(`workspace-id`),
    };
    this.update({modalShowing: true});
    return promptInlineCohorts(attrs);
  }

  private addSelectedItemToRecent(item) {
    if (this.isInlineCohort(item) || (item.customProperty && !isPersistedCustomProperty(item.customPropertyId))) {
      return;
    }

    this.addRecentItem(item.resourceType, pick(item, PropertyItemPersistedIdentifierKeys));
  }

  private selectItem(
    item,
    {
      rawItem = null,
      // Was an item in the properties menu clicked?
      onItemClicked = false,
      // Indicates whether the item selection is user triggered or automatically triggered (e.g.
      // custom properties / cohorts store callback). Note that this is not just the inverse of
      // onItemClicked. For example, if a user is creating a custom property via. the modal
      // autoTriggered will be false, and onItemClicked will also be false (since an item wasn't
      // clicked in the menu)
      autoTriggered = false,
      commit = false,
      searchFilter,
      labelMatch,
      subLabelMatch,
      customMatch,
    }: {
      rawItem?: any;
      onItemClicked?: boolean;
      autoTriggered?: boolean;
      commit?: boolean;
      searchFilter?: string;
      labelMatch?: SearchMatchResult;
      subLabelMatch?: SearchMatchResult;
      customMatch?: SearchMatchResult;
    } = {},
  ) {
    // Pickout undefined values to prevent duplicate actions triggering
    let selection = pickBy(item, (value) => value !== undefined);

    if (
      this.attr(`show-special-date-property`) &&
      !item.unit &&
      item.propertyDefaultType === PropertyType.Datetime &&
      item.type === PropertyType.Datetime && // if not purposefully typecasted
      !this.attr(`hide-special-date-property-unit`) &&
      !autoTriggered
    ) {
      return this.updateStagedProperty(rawItem, StagedMenuTypes.Datetime);
    }

    if (item.type === PropertyType.Dimension) {
      return this.updateStagedProperty(rawItem, StagedMenuTypes.Dimension);
    }

    if (this.isMultipleSelection()) {
      // if item was not removed, add it to the selection
      const baseSelection = this.getArraySelection();
      selection = baseSelection.filter((selectedItem) =>
        selectedItem.value &&
        isSerializedInlineCohort(selectedItem.value.cohort) &&
        isSerializedInlineCohort(item.value.cohort)
          ? !isEqual(selectedItem.value.cohort.raw_cohort, item.value.cohort.raw_cohort)
          : selectedItem.value !== item.value,
      );
      if (baseSelection.length === selection.length) {
        selection.push(pick(item, PropertyItemKeys));
      }
    }

    this.addSelectedItemToRecent(item);
    const detail = {item: selection, rawItem, onItemClicked, autoTriggered, commit};
    this.dispatchEvent(new CustomEvent(`select`, {detail}));

    if (onItemClicked) {
      this.trackSelection(
        {
          eventName: TrackingEvent.SelectProperty,
          props: {
            'Selected label': selection.label,
            'Selected value': selection.value,
            'Selected resource type': selection.resourceType,
            'is custom property': !!selection.customProperty,
            'is hidden entity': !!rawItem.hidden,
            'Search - filter': searchFilter,
            'Search - label match': labelMatch,
            'Search - sublabel match': subLabelMatch,
            'Search - custom match': customMatch,
            'Metadata Search Enabled': true,
          },
        },
        false,
      ); // Making this not cancelable so it's always tracked. https://mixpanel.slack.com/archives/C0KN5EVB8/p1604641156158700
    }
  }

  private replaceItem(
    item,
    index,
    {
      rawItem = null,
      onItemClicked = false,
      commit = false,
      autoTriggered = false,
    }: {rawItem?: any; onItemClicked?: boolean; commit?: boolean; autoTriggered?: boolean} = {},
  ) {
    // Pickout undefined values to prevent duplicate actions triggering
    let selection = pickBy(item, (value) => value !== undefined);

    if (this.isMultipleSelection()) {
      // if item was not removed, add it to the selection
      const baseSelection = this.getArraySelection();
      baseSelection[index] = pick(item, PropertyItemKeys);

      selection = baseSelection;
    }

    this.addSelectedItemToRecent(item);
    const detail = {item: selection, rawItem, commit, autoTriggered};
    this.dispatchEvent(new CustomEvent(`select`, {detail}));

    if (onItemClicked) {
      this.trackSelection({
        eventName: TrackingEvent.SelectProperty,
        props: {},
      });
    }
  }

  /**
   * Appends the selected type to the staged property
   */
  private applyStagedValue(item) {
    const {stagedProperty, stagedMenuType} = this.state;

    this.update({stagedProperty: null});

    if (stagedMenuType === StagedMenuTypes.Typecast) {
      this.onItemClick({
        ...stagedProperty,
        type: item.value,
      });
    } else if (stagedMenuType === StagedMenuTypes.Datetime) {
      this.onItemClick({
        ...stagedProperty,
        unit: item.value,
      });
    } else if (stagedMenuType === StagedMenuTypes.Dimension) {
      this.onItemClick({
        ...stagedProperty,
        value: item.value,
        type: item.type,
        propertyDefaultType: item.type,
        joinPropertyType: stagedProperty.joinPropertyType,
        ...(stagedProperty.dimensionDataGroupId && Array.isArray(item.value)
          ? {dataGroupId: stagedProperty.dimensionDataGroupId}
          : {}),
        ...(stagedProperty.tempDataGroupId && Array.isArray(item.value)
          ? {tempDataGroupId: stagedProperty.tempDataGroupId}
          : {tempDataGroupId: undefined}),
      });
    } else {
      throw new Error(`Invalid staged menu type ${stagedMenuType}`);
    }
  }

  private fetchData() {
    this.fetchPeopleProperties();
    this.fetchEventProperties();

    this.fetchDataGroups();
    this.fetchLookupTables();
    this.fetchCohorts();
    this.fetchUserDetails();
    this.fetchPropertyDefinitions();
    this.fetchProjectFeatureFlags();
  }

  private getPropertyResourceTypes() {
    return compact([
      this.attr(`project-has-people`) ? ResourceType.User : null,
      !this.attr(`hide-events`) ? ResourceType.Event : null,
    ]);
  }

  private fetchPropertyDefinitions() {
    for (const type of this.getPropertyResourceTypes()) {
      const params = {
        category: type,
        projectId: this.projectId,
        includeCustom: this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None,
        workspaceId: this.workspaceId,
      };
      PropertyDefinitionStoreMap[type].fetch(params);
    }
  }

  /**
   * TODO for perf purposes, infra team does not want standard people props list available to filter when a
   * B2B data group has been selected for the main analysis. Hopefully we can remove this restriction
   * eventually, as there are legit analysis cases that it enables.
   */
  private shouldShowUserProps() {
    return this.attr(`project-has-people`) && !this.hasSelectedDataGroup();
  }

  private hasSelectedDataGroup() {
    return this.attr(`all-data-groups`) && !isEmpty(this.attr(`data-group-id`));
  }

  private shouldShowCohorts() {
    return (
      this.attr(`inline-cohorts-override`) ||
      (this.attr(`project-has-cohorts`) && (!this.hasSelectedDataGroup() || this.attr(`support-b2b-cohorts`)))
    );
  }

  private getPropertyDefinitions() {
    return this.getPropertyResourceTypes().reduce((acc, type) => {
      const definitions = Object.values(PropertyDefinitionStoreMap[type].value);

      // convert data definition information about custom properties into an
      // id -> definition map, we need this map below to merge the most up-to-date
      // information from the property store with information that only exists
      // in the definition, like query counts
      const customDefinitionsMap = definitions
        .filter((definition) => definition.status === `custom`)
        .reduce((acc, def) => {
          acc[def.customPropertyId] = def;
          return acc;
        }, {});

      const inlineCustomPropertyIds = Object.keys(customPropertiesStore.value).filter((customPropertyId) => {
        return (
          !isPersistedCustomProperty(customPropertyId) &&
          !customPropertiesStore.value[customPropertyId].newCustomPropertyId
        );
      });

      const createdCustomPropertyIds = Object.keys(customPropertiesStore.value).filter(
        (customPropertyId) =>
          isPersistedCustomProperty(customPropertyId) &&
          !definitions.find((definition) => definition.customPropertyId === customPropertyId),
      );

      // add in definitions from newly created and inline custom properties
      const customDefinitions = [...createdCustomPropertyIds, ...inlineCustomPropertyIds]
        .filter((customPropertyId) => {
          const customProperty = customPropertiesStore.value[customPropertyId];
          return CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING[customProperty.resourceType] === type;
        })
        .map((customPropertyId) => {
          const customProperty = customPropertiesStore.value[customPropertyId];
          const definition = customDefinitionsMap[customPropertyId];
          return {
            ...definition,
            name: customProperty.name || definition?.name,
            description: customProperty.description || definition?.description,
            customPropertyId,
            customProperty,
            resourceType: CUSTOM_PROPERTY_RESOURCE_TYPE_MAPPING[customProperty.resourceType],
            status: `custom`,
            type: customProperty.propertyType,
          } as PropertyDefinition;
        });

      const regularDefinitions = definitions.filter((definition) => definition.status !== `custom`);

      acc[type] = [...customDefinitions, ...regularDefinitions];

      return acc;
    }, {});
  }

  private getTooltipData(item: Cohort | MpProperty, resourceType: ResourceType): ContextPanelData {
    if (!item) {
      return null;
    }

    const canWriteDataDefinition =
      currentProjectStore.value?.hasPermission(`write_data_definitions`) ||
      this.state.permissionList.includes(`write_data_definitions`);

    const isCohort = resourceType === ResourceType.Cohort;

    if (isCohort) {
      const allowDescriptionEdit = !this.attr(`description-edit-disabled`);
      return getTooltipDataForCohort(item as Cohort, {allowDescriptionEdit});
    } else {
      const propertyDefinitions = this.getPropertyDefinitions();
      const definitions = propertyDefinitions[resourceType];
      const allowDescriptionEdit = !this.attr(`description-edit-disabled`) && canWriteDataDefinition;
      if (Object.prototype.hasOwnProperty.call(propertyDefinitions, resourceType)) {
        return getTooltipDataForProperty(item as MpProperty, definitions, {
          allowEdit: !this.attr(`hide-edit-controls`),
          allowDescriptionEdit,
          lookupTables: lookupTablesStore.value,
        });
      }
    }
    return null;
  }

  private showItemDetails(item) {
    // For some reason, in some cases (specifically, the hold property constant feature) this
    // showItemDetails function is triggered after onItemClick. The consequence is that we
    // continue showing the tooltip even after an item is selected from the menu. To work around
    // this, check if the menu is still open (it won't be open if onItemClick has already been
    // triggered), and don't show the tooltip if the menu is closed.
    if (!this.attr(`open`) || !item) {
      return;
    }

    // because of the above, the tooltip goes above the cohort filter when directly being created
    // from the cohort filter. So don't display the tooltip if we are in the specific case where
    // we have one cohort and the user attempts to edit the cohort directly from query-section
    if (this.state.modalShowing) {
      return;
    }

    let tooltipData;
    // show context panel data when data definitions are ready
    if (item.tooltipData) {
      tooltipData = item.tooltipData;
    } else if (item.isDisabled) {
      tooltipData = null;
    } else {
      tooltipData = this.getTooltipData(item, item.resourceType);
    }
    this.update({tooltipData});
  }

  private getSelectedItem() {
    const selected = this.attr(`selected`);
    return Array.isArray(selected) ? selected[0] : selected;
  }

  private getSelectedResourceType(): ResourceType {
    const selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      return null;
    }
    // TODO(mack): Cohort query entries in Insights are missing resourceType pending
    // https://github.com/mixpanel/analytics/pull/27358, so assume Cohort in this case for now.
    // Remove this when not needed anymore.
    return selectedItem.resourceType || ResourceType.Cohort;
  }

  public getSelectedHoverTooltipData(): ContextPanelData {
    const selected = this.attr(`selected`);
    if (!selected || (Array.isArray(selected) && selected.length > 1)) {
      return null;
    }

    const selectedItem = this.getSelectedItem();
    const resourceType = this.getSelectedResourceType();
    const selectedItemTooltipData = selected.tooltipData || SPECIAL_PROPERTIES.get(selected?.value)?.tooltipData;

    if (selectedItemTooltipData) {
      return selectedItemTooltipData;
    } else if ([ResourceType.User, ResourceType.Event].includes(resourceType)) {
      const {customPropertyId} = selectedItem;
      const properties = resourceType === ResourceType.Event ? this.getEventProperties() : this.getUserProperties();
      const property = properties.find((p) => p.name === selectedItem.value);
      if (property && this.isPropertyDisabled(property, selectedItem.value)) {
        return null;
      }

      const mpProperty: MpProperty = {
        name: selectedItem.value,
        ...(customPropertyId ? {customPropertyId} : {}),
      };

      return this.getTooltipData(mpProperty, resourceType);
    } else if (resourceType === ResourceType.Cohort) {
      const cohortId = Number.isInteger(selectedItem.value)
        ? selectedItem.value
        : selectedItem.value.cohort.raw_cohort.unsavedId;
      const cohortItem = this.transformCohortToCohortItem(cohortsStore.value[cohortId]);
      return this.getTooltipData(cohortItem, resourceType);
    }

    return null;
  }

  public getSelectedTrackingProps(): Obj {
    const selectedItem = this.getSelectedItem();
    if (!selectedItem) {
      return null;
    }

    const props = {};
    const resourceType = this.getSelectedResourceType();
    if (resourceType === ResourceType.Cohort) {
      props[`Entity type`] = `cohort`;
      if (selectedItem.value && !Number.isInteger(selectedItem.value)) {
        props[`Is inline`] = true;
      }
    } else {
      props[`Entity type`] = `property`;
      props[`Resource type`] = resourceType;
      if (selectedItem.customPropertyId) {
        props[`Is custom`] = true;
        if (!isPersistedCustomProperty(selectedItem.customPropertyId)) {
          props[`Is inline`] = true;
        }
      }
    }
    return props;
  }

  isInlineCohort(item: PropertyItem) {
    return Boolean(item.value?.cohort?.raw_cohort);
  }

  handleTooltipContextAction(ev): TooltipContextActionResult {
    const {name, item} = ev.detail;
    const isCustomProperty = name === `editCustomProperty`;
    const isCohortOnFeatureFlag =
      name === `editCohort` && featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.InlineCohorts);
    const isInlineCohort = this.isInlineCohort(item);
    // prevent the context menu from showing up after selecting a property
    this.helpers.closeModal();

    if (isCustomProperty || isInlineCohort || isCohortOnFeatureFlag) {
      if (isCustomProperty) {
        const modalType =
          item.value === SPECIAL_CONTROLS.CREATE_CUSTOM_BEHAVIORAL_PROPERTY.value
            ? MODAL_TYPE.BehavioralCustomProperty
            : MODAL_TYPE.CustomProperty;
        this.clickEditCustomProperty(item.property, modalType);
      } else {
        this.editCohort(item);
      }

      ev.stopPropagation();
      return {handled: true, openedModal: true};
    }
    return null;
  }

  async handleShareTooltipContextAction(ev) {
    const {name, item} = ev.detail;
    const isShareCohort = name === `shareCohort`;

    const {eventName, props} = shareTrack.shareButtonClick(ShareButtonUISource.ContextPanel);
    this.trackEvent(eventName, props, {prefix: shareEventPrefix});

    let allowOverrideShare = true;
    if (item.superadminOverrideShare) {
      allowOverrideShare = await promptSuperadminOverrideShare(
        isShareCohort ? EntityType.Cohort : EntityType.CustomProperty,
      );
    }
    if (allowOverrideShare) {
      promptSharing({
        type: isShareCohort ? SharableEntityType.Cohort : SharableEntityType.CustomProperty,
        id: isShareCohort ? item.id : item.property.customPropertyId,
        'project-id': this.attr(`project-id`),
      });
    }
  }

  hasOpenedModal() {
    return false;
  }

  private getRecentlyViewedSection(resourceTypes, sections, limitPerKey = null): PropertiesMenuSection {
    const allSectionsItems = sections.reduce((a, b) => [...a, ...(b.items || [])], []);

    // hide recently viewed section when searching
    if (this.helpers.getSearchFilter()) {
      return null;
    }

    return {
      label: `Recently viewed`,
      sectionType: PropertiesMenuSectionType.Recent,
      items: resourceTypes.reduce((items, resourceType) => {
        const recentItems = this.getRecentItems(resourceType)
          .map((persistedItem) => {
            const propertyItem = allSectionsItems.find((item) => {
              return this.getItemUniqueKey(item) === this.getItemUniqueKey(persistedItem);
            });
            if (!propertyItem || SPECIAL_PROPERTIES.has(propertyItem.value)) {
              return null;
            }

            return propertyItem;
          })
          .filter(Boolean);

        if (limitPerKey) {
          recentItems.splice(limitPerKey);
        }

        return [...items, ...recentItems];
      }, []),
    };
  }

  /**
   * If `selected` = [`A`, `B`, `C`, `D`] and the properties menu items are just [`A`, `B`], unmapped selected properties are [`C`, `D`].
   */
  private getMenuSectionWithUnmappedSelected(sections) {
    const allSectionsItems = sections.reduce((a, b) => [...a, ...(b.items || [])], []);
    let selected = this.attr(`selected`) || [];
    selected = Array.isArray(selected) ? selected : [selected];

    const selectedFromThisTab = selected.filter(({resourceType, dataGroupId}) =>
      [dataGroupId, resourceType].includes(this.state.selectedTab),
    );
    const unmappedSelected = selectedFromThisTab.filter((item) => !this.isItemSelected(item.value, allSectionsItems));

    if (!unmappedSelected.length) {
      return sections;
    }
    // TODO(Gonzalo): We will use type-text icon until we have a defined way to specify the type (user, event) of the property the user selected.
    const icon = `type-text`;

    const extraItems = unmappedSelected.map((ex) => ({
      ...ex,
      isSelected: true,
      label: typeof ex.value === `string` ? ex.value : ex.label,
      icon,
    }));
    if (sections.length === 1) {
      return [
        {
          ...sections[0],
          items: [...sections[0].items, ...extraItems],
        },
      ];
    }
    return [
      ...sections,
      {
        items: extraItems,
      },
    ];
  }

  private getMenuSectionWithRecentlyViewed(
    resourceTypes: ResourceType[],
    sections,
    limitPerKey = null,
  ): PropertiesMenuSection[] {
    return [
      this.getRecentlyViewedSection(resourceTypes, sections, limitPerKey),
      {
        isLoading: this.state.loadingEventProperties,
        items: this.getPropertyMenuItems({
          properties: this.getCustomFormulas([ResourceType.Event, ResourceType.User]),
        }),
        label: `Formulas in this report`,
        showLimit: MENU_ITEM_LIMIT,
        sectionType: PropertiesMenuSectionType.Other,
      },
      ...sections,
    ].filter(Boolean);
  }

  /**
   * Returns all sections to be rendered in the selection menu
   * based on the attribute configuration
   */
  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getMenuSections() {
    const {selectedTab} = this.state;
    const projectHasDataGroups = this.attr(`all-data-groups`);
    const projectHasEvents = !this.attr(`hide-events`);
    const hasOtherOptions = this.attr(`other-options`).length > 0;
    const showRecentlyViewed = !this.attr(`hide-recently-viewed`);

    let sections;
    switch (selectedTab) {
      case DefaultToggleTab:
        sections = [
          ...(hasOtherOptions ? [this.getOtherMenuSection()] : []),
          ...(projectHasEvents ? this.getEventsMenuSections() : []),
          ...(this.shouldShowUserProps() ? this.getUserMenuSections() : []),
          ...(projectHasDataGroups ? this.getDataGroupsSections() : []),
          ...(this.shouldShowCohorts() ? this.getCohortsMenuSections() : []),
        ];
        if (showRecentlyViewed) {
          sections = this.getMenuSectionWithRecentlyViewed(
            [ResourceType.Event, ResourceType.User, ResourceType.Other],
            sections,
            2,
          );
        }
        break;
      case ResourceType.Event:
        sections = projectHasEvents ? this.getEventsMenuSections() : [];
        if (sections.length > 0 && showRecentlyViewed) {
          sections = this.getMenuSectionWithRecentlyViewed([ResourceType.Event], sections);
        }
        break;
      case ResourceType.User:
        sections = this.shouldShowUserProps() ? this.getUserMenuSections() : [];
        if (sections.length > 0 && showRecentlyViewed) {
          sections = this.getMenuSectionWithRecentlyViewed([ResourceType.User], sections);
        }
        break;
      case ResourceType.Cohort:
        sections = this.shouldShowCohorts() ? this.getCohortsMenuSections() : [];
        break;
      default:
        sections = projectHasDataGroups ? this.getDataGroupsSections(selectedTab) : [];
    }

    if (this.isMultipleSelection()) {
      sections = this.getMenuSectionWithUnmappedSelected(sections);
    }

    if (this.state.getSortedMenuSections) {
      sections = this.state.getSortedMenuSections(sections);
    }

    if (this.hasLookupTables() && !this.helpers.getSearchFilter()) {
      // remove lookup table properties when there is no search filter (except in the "Recent" section)
      sections = this.filterLookupTablePropertiesFromSections(sections);
    }

    return sections;
  }

  private getEventProperties(includeHidden = false) {
    let eventProperties;
    if (featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.DataDefinitionsFetch)) {
      // we filter out custom properties here to prevent double count. custom properties from data definitions store & custom properties store are stitched together in getPropertyDefinitions
      // eventually, we want to abstract the stitching to the definitions store
      eventProperties = Object.values(PropertyDefinitionStoreMap[ResourceType.Event].value)
        .filter((prop) => prop.status !== `custom`)
        .map(getDataDefinitionsPropertyMenuItem);
    } else {
      eventProperties =
        this._eventPropertySource === EventPropertySource.Store
          ? eventPropertiesStore.value[this._eventPropertiesStoreKey] || []
          : this.state.eventProperties;
    }
    eventProperties = this.addTempMappingsToProperties(ResourceType.Event, eventProperties);

    if (!includeHidden) {
      return eventProperties.filter((prop) => !prop.hidden);
    } else {
      return eventProperties;
    }
  }

  private hasLookupTables() {
    return Object.keys(lookupTablesStore.value).length !== 0;
  }

  private getLookupTableProperties(joinProperty) {
    let lookupTableProperties = [];
    const {
      dimensionDataGroupId,
      tempDataGroupId,
      joinPropertyType,
      name: joinPropertyName,
      label: joinPropertyLabel,
    } = joinProperty;
    const dataGroupId = tempDataGroupId || dimensionDataGroupId;

    const lookupProps = lookupTablesStore.value[dataGroupId]?.properties;
    if (lookupProps) {
      lookupTableProperties = lookupProps.map((lookupTableProp) => ({
        ...lookupTableProp,
        dataGroupId,
        dimensionDataGroupId: dataGroupId,
        joinPropertyType,
        joinPropertyLabel,
        subLabelTheme: `small`,
        value: [joinPropertyName, lookupTableProp.name],
        isLookupTableProperty: true,
      }));
    }
    return lookupTableProperties;
  }

  private getAllLookupTableProperties(properties: any[]): any[] {
    if (!this.hasLookupTables()) {
      return [];
    }

    let lookupTableProperties = [];
    for (const prop of properties) {
      if (prop.type === PropertyType.Dimension) {
        lookupTableProperties = [...lookupTableProperties, ...this.getLookupTableProperties(prop)];
      }
    }
    return lookupTableProperties;
  }

  private filterLookupTablePropertiesFromSections(sections: PropertiesMenuSection[]) {
    return sections.map((section) => {
      if ([PropertiesMenuSectionType.Event, PropertiesMenuSectionType.User].includes(section.sectionType)) {
        section.items = section.items.filter((item) => !item.isLookupTableProperty);
        return section;
      } else {
        return section;
      }
    });
  }

  private getLookupTablePropertyFromSelected(selected: PropertyItem) {
    let properties = [];
    if (selected.resourceType === ResourceType.Event) {
      properties = this.getEventProperties();
    } else if (selected.resourceType === ResourceType.User) {
      properties = this.getUserProperties();
    }
    return properties.find((prop) => {
      return prop.name === selected.value[0];
    });
  }

  private getEventsMenuSections(): PropertiesMenuSection[] {
    const specialEventProperties = [];
    let properties = this.getEventProperties();
    properties = [...properties, ...this.getAllLookupTableProperties(properties)];

    if (
      this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None &&
      this._eventPropertySource !== EventPropertySource.Empty
    ) {
      // workspace filters can use inline custom properties, but cannot refer to persisted custom properties
      if (!this.attr(`enable-workspace-filter-restrictions`)) {
        properties = sortPropertyItems([...properties, ...this.getCustomProperties(ResourceType.Event)]);
      }
      if (!this.attr(`hide-edit-controls`)) {
        specialEventProperties.push(SPECIAL_CONTROLS.CREATE_CUSTOM_EVENT_FORMULA);
      }
    }

    if (this.attr(`show-special-date-property`)) {
      specialEventProperties.push(DATE_PROPERTY);
    }

    if (this.attr(`show-special-dataset-property`)) {
      specialEventProperties.push(DATASET_PROPERTY);
    }

    // Surface special properties to the top
    properties = [...specialEventProperties, ...properties];

    return [
      {
        isLoading: this.state.loadingEventProperties,
        items: this.getPropertyMenuItems({properties, resourceType: ResourceType.Event}),
        label: this.attr(`hide-section-labels`) ? null : `Event properties`,
        showLimit: MENU_ITEM_LIMIT,
        sectionType: PropertiesMenuSectionType.Event,
        isHidden: false,
      },
      ...this.getHiddenEventsMenuSections(),
    ];
  }

  private getHiddenEventsMenuSections() {
    const hiddenProperties = Object.values(PropertyDefinitionStoreMap[ResourceType.Event].value).filter(
      (def) => def.hidden,
    );

    return [
      {
        isLoading: this.state.loadingEventProperties,
        items: this.getPropertyMenuItems({properties: hiddenProperties, resourceType: ResourceType.Event}),
        label: `Hidden event properties`,
        showLimit: MENU_ITEM_LIMIT,
        sectionType: PropertiesMenuSectionType.Event,
        isHidden: !this.state.includeHiddenEventProps,
      },
    ];
  }

  private isVisibleCustomPropertyForEvents(customProperty): boolean {
    const eventNames = this.eventNames();

    if (this.includeAllProperties(eventNames)) {
      return true;
    }

    const eventProperties = new Set(this.getEventProperties(true).map((prop) => prop.name));

    // $date is a special event property which exists on all events, but is not always returned
    // by getEventProperties
    eventProperties.add(`$date`);

    if (isPersistedCustomProperty(customProperty.customPropertyId)) {
      const referencedEventProperties = customProperty.referencedRawEventProperties || [];
      // persisted custom properties know from the backend response all the transitively
      // referenced raw event properties
      return (
        referencedEventProperties.length === 0 ||
        Object.values(customProperty.referencedRawEventProperties).some((property: string) =>
          eventProperties.has(property),
        )
      );
    } else {
      const hasUserPropertiesOnly = Object.values(customProperty.composedProperties).every(
        (prop: PropertyItem) => prop.resourceType === ResourceType.User,
      );
      // for non-persisted custom properties we need to compute the transitively referenced
      // raw event properties by combining the referenced properties of any nested custom events
      // and any properties referenced directly by this property
      return (
        hasUserPropertiesOnly ||
        Object.values(customProperty.composedProperties).some((composedProperty: any) => {
          if (composedProperty.customPropertyId) {
            const referencedEventProperties =
              customPropertiesStore.value[composedProperty.customPropertyId].referencedRawEventProperties;
            return (
              referencedEventProperties.length === 0 ||
              referencedEventProperties.some((property: string) => eventProperties.has(property))
            );
          } else {
            return composedProperty.resourceType === ResourceType.Event && eventProperties.has(composedProperty.value);
          }
        })
      );
    }
  }

  private isVisibleCustomProperty(customPropertyId: string): boolean {
    if (!isPersistedCustomProperty(customPropertyId)) {
      return true;
    }

    const id = isPersistedCustomProperty(customPropertyId) ? parseInt(customPropertyId) : customPropertyId;
    const customProperty = customPropertiesStore.value[id];
    const property = storeCustomPropertyToProperty(customPropertyId);

    // exclude persisted behavioral props
    if (
      this.attr(`include-custom-properties`) === IncludeCustomPropertyType.PersistedNonBehavioralOnly &&
      isBehavioralCustomProperty(customProperty)
    ) {
      return false;
    }

    if (property.resourceType === ResourceType.Event && !this.isVisibleCustomPropertyForEvents(customProperty)) {
      return false;
    }

    return customProperty.isVisible || customProperty.user === this.attr(`user-id`);
  }

  private getCustomEntities(resourceTypes: ResourceType[]) {
    return Object.keys(customPropertiesStore.value)
      .filter((customPropertyId) => !customPropertiesStore.value[customPropertyId].newCustomPropertyId)
      .filter(
        (customPropertyId) =>
          this.attr(`include-custom-properties`) === IncludeCustomPropertyType.All ||
          isPersistedCustomProperty(customPropertyId),
      )
      .filter((customPropertyId) => this.isVisibleCustomProperty(customPropertyId))
      .map((customPropertyId) => {
        const property = storeCustomPropertyToProperty(customPropertyId);

        return this.getPropertyMenuItem({
          property,
          resourceType: property.resourceType,
        });
      })
      .filter((item) => resourceTypes.includes(item.resourceType));
  }

  private getCustomProperties(resourceType: ResourceType) {
    return this.getCustomEntities([resourceType]).filter((item) => isPersistedCustomProperty(item.customPropertyId));
  }

  private getCustomFormulas(resourceTypes: ResourceType[]) {
    return this.getCustomEntities(resourceTypes).filter((item) => !isPersistedCustomProperty(item.customPropertyId));
  }

  private getUserProperties() {
    // return a shallow copy so that callers do not accidentally mutate the store
    let userProperties;
    if (featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.DataDefinitionsFetch)) {
      // we filter out custom properties here to prevent double count. custom properties from data definitions store & custom properties store are stitched together in getPropertyDefinitions
      // eventually, we want to abstract the stitching to the definitions store
      userProperties = Object.values(PropertyDefinitionStoreMap[ResourceType.User].value)
        .filter((prop) => prop.status !== `custom`)
        .map(getDataDefinitionsPropertyMenuItem);
    } else {
      userProperties = peoplePropertiesStore.value;
    }

    userProperties = this.shouldShowUserProps()
      ? this.addTempMappingsToProperties(ResourceType.User, userProperties)
      : [];

    return userProperties;
  }

  private isFirstEventTimeEnabled(): boolean {
    // first event time is backed by a custom behavioral property:
    // - behavioral properties are not allowed in workspace filters
    // - first event time is only valid in situations where custom properties are valid
    return (
      !this.attr(`enable-workspace-filter-restrictions`) &&
      ![IncludeCustomPropertyType.PersistedNonBehavioralOnly, IncludeCustomPropertyType.None].includes(
        this.attr(`include-custom-properties`),
      )
    );
  }

  private isBehavioralPropertiesEnabled(): boolean {
    // behavioral properties cannot be used in workspace filters
    return (
      featuresStore.value?.hasProjectFeatureFlag(ProjectFeatureFlag.BehavioralProperties) &&
      !this.attr(`enable-workspace-filter-restrictions`)
    );
  }

  private getUserMenuSections(): PropertiesMenuSection[] {
    let properties = this.getUserProperties();
    properties = [
      // peoplePropertiesStore properties
      ...properties,
      // all mapped profile properties
      ...this.getAllLookupTableProperties(properties),
      // special first event time property
      ...(this.isFirstEventTimeEnabled() ? [FIRST_EVENT_TIME_PROPERTY] : []),
    ];

    // workspace filters can use inline custom properites, but cannot refer to persisted custom properties
    if (
      this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None &&
      !this.attr(`enable-workspace-filter-restrictions`)
    ) {
      properties = sortPropertyItems([...properties, ...this.getCustomProperties(ResourceType.User)]);
    }

    if (this.attr(`include-custom-properties`) !== IncludeCustomPropertyType.None && !this.attr(`hide-edit-controls`)) {
      properties = [
        ...(this.isBehavioralPropertiesEnabled() ? [SPECIAL_CONTROLS.CREATE_CUSTOM_BEHAVIORAL_PROPERTY] : []),
        SPECIAL_CONTROLS.CREATE_CUSTOM_PEOPLE_FORMULA,
        ...properties,
      ];
    }

    /*
        TODO (duc): this is a quick patch (which hide the dim prop instead of showing it as a normal prop)
        Need to make user properties re-fecth correctly
      */
    if (this.attr(`exclude-dimension-properties`)) {
      properties = properties.filter((prop) => prop.type !== `dimension`);
    }
    return [
      {
        isLoading: this.state.loadingPeopleProperties,
        items: this.getPropertyMenuItems({properties, resourceType: ResourceType.User}),
        label: this.attr(`hide-section-labels`) ? null : `Profile properties`,
        showLimit: MENU_ITEM_LIMIT,
        sectionType: PropertiesMenuSectionType.User,
        isHidden: false,
      },
      ...this.getHiddenUserMenuSections(),
    ];
  }

  private getHiddenUserMenuSections() {
    let hiddenProperties = Object.values(PropertyDefinitionStoreMap[ResourceType.User].value).filter(
      (def) => def.hidden,
    );
    /*
      TODO (duc): this is a quick patch (which hide the dim prop instead of showing it as a normal prop)
      Need to make user properties re-fecth correctly
    */
    if (this.attr(`exclude-dimension-properties`)) {
      hiddenProperties = hiddenProperties.filter((prop) => prop.type !== `dimension`);
    }
    return [
      {
        isLoading: this.state.loadingPeopleProperties,
        items: this.getPropertyMenuItems({properties: hiddenProperties, resourceType: ResourceType.User}),
        label: `Hidden profile properties`,
        showLimit: MENU_ITEM_LIMIT,
        sectionType: PropertiesMenuSectionType.User,
        isHidden: !this.state.includeHiddenUserProps,
      },
    ];
  }

  private isYourCohort(cohort) {
    return cohort.createdBy && cohort.createdBy.id === this.attr(`user-id`);
  }

  private transformCohortToCohortItem(cohort: SerializedCohort | SerializedInlineCohort): CohortItem {
    if (isSerializedInlineCohort(cohort as SerializedInlineCohort)) {
      const unsavedCohort = cohort as SerializedInlineCohort;

      // Hide converted cohorts if the saved cohort exists in store
      if (
        unsavedCohort.raw_cohort.unsavedId &&
        Number.isInteger(unsavedCohort.id) &&
        cohortsStore.value[unsavedCohort.id]
      ) {
        return null;
      }

      // Unsaved cohort does not contain permissions (e.g. canView)
      return {
        label: unsavedCohort.name,
        name: unsavedCohort.name,
        value: {
          cohort: {
            ...unsavedCohort,
          },
        },
        createdBy: unsavedCohort.created_by,
      };
    }

    const savedCohort = cohort as SerializedCohort;

    return {
      id: savedCohort.id,
      label: savedCohort.name,
      value: savedCohort.id,
      name: savedCohort.name,
      count: savedCohort.count,
      description: savedCohort.description,
      dataGroupId: savedCohort.data_group_id,
      createdBy: savedCohort.created_by,
      lastEdited: savedCohort.last_edited,
      can_update_basic: savedCohort.can_update_basic,
      canView: savedCohort.can_view,
      canUpdateBasic: savedCohort.can_update_basic,
      canShare: savedCohort.can_share,
      isSuperadmin: savedCohort.is_superadmin,
      allowStaffOverride: savedCohort.allow_staff_override,
    };
  }

  private getSerializedCohorts() {
    return sortBy(cohortsStore.value, `name`)
      .filter((cohort: SerializedCohort | SerializedInlineCohort) => {
        const dataGroupId = this.attr(`data-group-id`);
        const cohortDataGroupId = isSerializedInlineCohort(cohort)
          ? cohort.raw_cohort.data_group_id
          : cohort.data_group_id;
        return dataGroupIDsEqual(dataGroupId, cohortDataGroupId ? cohortDataGroupId.toString() : ``);
      })
      .map((cohort: SerializedCohort | SerializedInlineCohort) => this.transformCohortToCohortItem(cohort))
      .filter(Boolean);
  }

  private getCohortsMenuSections(): PropertiesMenuSection[] {
    const cohorts = this.getSerializedCohorts();

    let inlineCohorts;
    let [userCohorts, allCohorts] = partition(cohorts, (cohort) => this.isYourCohort(cohort));
    // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
    [inlineCohorts, userCohorts] = partition(
      this.getPropertyMenuItems({
        properties: userCohorts,
        resourceType: ResourceType.Cohort,
      }),
      (cohort) => cohort.value && cohort.value.cohort && cohort.value.cohort.raw_cohort,
    );

    allCohorts = allCohorts.filter((cohort) => cohort.canView);
    // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
    allCohorts = this.getPropertyMenuItems({
      properties: allCohorts,
      resourceType: ResourceType.Cohort,
    });

    // defines cohorts that were last selected and currently selected, the culprit to check if a cohort goes all the way
    // to the bottom without being categorized for some reason
    const currentlySelected = intersectionBy(this.attr(`selected`), this.state.lastMultiSelection, `value`);
    const selectedCohorts = this.getPropertyMenuItems({
      properties: cohorts,
      resourceType: ResourceType.Cohort,
      selected: currentlySelected,
    }).filter((cohort) => cohort.isSelected);

    if (this.attr(`show-all-users-cohort`)) {
      // @ts-ignore - TODO: Type 'PropertyItem[]' is not assignable to type 'Cohort[]'.
      allCohorts.unshift(ALL_USERS_COHORT);
    }

    return [
      {
        isLoading: this.state.loadingCohorts,
        items: selectedCohorts,
        label: `Selected Cohorts`,
        sectionType: PropertiesMenuSectionType.Cohort,
      },
      {
        isLoading: this.state.loadingCohorts,
        items: [
          ...(this.attr(`project-has-cohorts`) || this.attr(`inline-cohorts-override`)
            ? [SPECIAL_CONTROLS.CREATE_INLINE_COHORT]
            : []),
          ...inlineCohorts,
          ...userCohorts,
        ],
        label: `Your Cohorts`,
        sectionType: PropertiesMenuSectionType.Cohort,
      },
      {
        isLoading: this.state.loadingCohorts,
        // @ts-ignore - TODO: Type 'CohortItem[]' is not assignable to type 'PropertyItem[]'.
        items: allCohorts,
        label: `All Cohorts`,
        showLimit: userCohorts.length > 0 ? MENU_ITEM_LIMIT : null,
        sectionType: PropertiesMenuSectionType.Cohort,
      },
    ];
  }

  private getOtherMenuSection(): PropertiesMenuSection {
    const items: PropertyItem[] = this.attr(`other-options`)
      .reduce((r, opt) => (OTHER_OPTIONS.has(opt) ? r.concat(OTHER_OPTIONS.get(opt)) : r), [])
      .map((item) => (this.isItemSelected(item.value, this.attr(`selected`)) ? {...item, isSelected: true} : item));
    return {
      isLoading: false,
      items,
      label: this.attr(`hide-section-labels`) ? null : `Other`,
      sectionType: PropertiesMenuSectionType.Other,
    };
  }

  /**
   * Generates items-menu sections from each loaded data group
   */
  private getDataGroupsSections(dataGroupId?: string): PropertiesMenuSection[] {
    const reportDataGroupId = this.attr(`data-group-id`);
    return this.getVisibleDataGroups(dataGroupId).map((group) => {
      const displayDataGroupFirstEventTime =
        this.isFirstEventTimeEnabled() && dataGroupIDsEqual(reportDataGroupId, group.groupID);
      const properties = sortPropertyItems([
        // all data group properties
        ...group.properties,
        /// special
        ...(displayDataGroupFirstEventTime ? [FIRST_EVENT_TIME_PROPERTY] : []),
      ]);
      return {
        isLoading: group.isLoading,
        items: this.getPropertyMenuItems({
          dataGroupId: group.groupID,
          properties,
          resourceType: ResourceType.User,
        }),
        label: this.attr(`hide-section-labels`) ? null : `${group.displayName} Properties`,
        showLimit: MENU_ITEM_LIMIT,
        sectionType: PropertiesMenuSectionType.DataGroup,
      };
    });
  }

  private getVisibleDataGroups(dataGroupId?: string): DataGroupItem[] {
    const {dataGroups} = this.state;
    const singleDataGroup = this.attr(`single-data-group`);
    let groups: DataGroupItem[] = dataGroupId
      ? dataGroups[dataGroupId]
        ? [dataGroups[dataGroupId]]
        : []
      : Object.values(dataGroups);

    if (singleDataGroup) {
      groups = groups.filter((group) => group.groupID === singleDataGroup);
    }

    return groups;
  }

  /**
   * Generates all tabs to be displayed in the selection menu
   */
  private getToggleTabs() {
    const projectHasEvents = !this.attr(`hide-events`);
    const showAllTab = this.attr(`show-all-tab`);

    let tabs = [
      ...(projectHasEvents ? [{value: ResourceType.Event, label: `Event`, disabled: false}] : []),
      ...(this.shouldShowUserProps() ? [{value: ResourceType.User, label: `User`, disabled: false}] : []),
      ...this.getVisibleDataGroups().map((group) => ({
        label: group.displayName,
        value: group.groupID,
        disabled: false,
      })),
      ...(this.shouldShowCohorts() ? [{value: ResourceType.Cohort, label: `Cohort`, disabled: false}] : []),
    ];

    if (showAllTab && tabs.length > 1) {
      tabs = [{value: DefaultToggleTab, label: `All`, disabled: false}, ...tabs];
    }

    return tabs;
  }

  private eventNames(): string[] {
    return this.attr(`event-names`) && uniq<string>(this.attr(`event-names`));
  }

  private includeAllProperties(eventNames: string[]): boolean {
    // if no events are passed, or events like `$all_events` are passed, all properties should be fetched
    return (
      !eventNames ||
      eventNames.length === 0 ||
      eventNames.some((eventName) => {
        return [SPECIAL_EVENTS.ALL_EVENTS, SPECIAL_EVENTS.ANY_EVENT, SPECIAL_EVENTS.TOP_EVENTS].includes(eventName);
      })
    );
  }

  /**
   * Fetches event properties for a set of events or all events
   */
  private async fetchEventProperties() {
    const eventNames = this.eventNames();
    const isPropertyIntersection = this.attr(`property-combine-mode`) === PropertyCombineMode.Intersection;
    const includeAllProperties = this.includeAllProperties(eventNames);

    this.update({loadingEventProperties: true});
    this._eventPropertiesStoreCallback = () => {
      this.update({loadingEventProperties: false});
    };

    let eventProperties = null;
    let params = null;
    let eventPropertySource;
    this._eventPropertiesStoreKey = ``;
    if (this.attr(`hide-events`) || (eventNames && eventNames.length === 0)) {
      eventProperties = [];
      eventPropertySource = EventPropertySource.Empty;
    } else if (!includeAllProperties && isPropertyIntersection) {
      const requestParamsList = eventNames.map((eventName) => ({
        eventName,
        includeDimensionProperties: !this.attr(`exclude-dimension-properties`),
        includeHidden: true,
        projectId: this.projectId,
        workspaceId: this.workspaceId,
      }));
      eventProperties = await Promise.all(
        requestParamsList.map((requestParams) => eventPropertiesClient.query(requestParams)),
      );
      eventProperties = calculatePropertiesIntersection(eventProperties);
      eventPropertySource = EventPropertySource.Intersection;
    } else {
      params = {
        ...(includeAllProperties ? {} : {eventName: eventNames}),
        includeDimensionProperties: !this.attr(`exclude-dimension-properties`),
        includeHidden: true,
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      };
      this._eventPropertiesStoreKey = Store.getKey(params);
      eventPropertySource = EventPropertySource.Store;
    }

    eventPropertiesStore.subscribe(this._eventPropertiesStoreCallback, this._eventPropertiesStoreKey);

    if (eventProperties) {
      this.update({eventProperties, loadingEventProperties: false});
    } else {
      eventPropertiesStore.fetch(params, {}, this._eventPropertiesStoreKey);
    }
    this._eventPropertySource = eventPropertySource;
  }

  private fetchPeopleProperties() {
    this.update({loadingPeopleProperties: true});
    this._peoplePropertiesStoreCallback = () => {
      this.update({loadingPeopleProperties: false});
    };
    peoplePropertiesStore.subscribe(this._peoplePropertiesStoreCallback);
    peoplePropertiesStore.fetch({
      includeDimensionProperties: !this.attr(`exclude-dimension-properties`),
      projectId: this.attr(`project-id`),
      workspaceId: this.attr(`workspace-id`),
    });
  }

  /**
   * Updates a given dataGroup with its list of properties
   */
  private async fetchDataGroupResourceProperties(resourceType: ResourceType, dataGroupId: string) {
    let properties = [];
    const dataGroup = this.state.dataGroups[dataGroupId];
    const params = {
      dataGroupId,
      projectId: this.projectId,
      workspaceId: this.workspaceId,
    };

    try {
      if (resourceType === ResourceType.User) {
        properties = await peoplePropertiesClient.query(params);
      } else if (resourceType === ResourceType.Event) {
        properties = await eventPropertiesClient.query(params);
      } else {
        throw new Error(`Failed to fetch properties for invalid resource type ${resourceType}`);
      }
    } catch (error) {
      console.error(`Error loading data group (${dataGroupId}) user properties`, error);
    }

    this.update({
      dataGroups: {
        ...this.state.dataGroups,
        [dataGroupId]: {
          ...dataGroup,
          isLoading: false,
          properties,
        },
      },
    });
  }

  /**
   * Fetches all data groups and triggers their
   * respective properties request
   */
  private async fetchDataGroups() {
    if (!this.attr(`all-data-groups`)) {
      return this.update({dataGroups: {}});
    }
    const dataGroups = {};

    const groups = await this.retrieveDataGroups();

    for (const group of groups) {
      const dataGroupId = group.groupID;
      const cachedProperties = peoplePropertiesClient.getCachedResponse({
        dataGroupId,
        projectId: this.projectId,
        workspaceId: this.workspaceId,
      });

      dataGroups[group.groupID] = {
        ...group,
        isLoading: cachedProperties ? false : true,
        properties: cachedProperties || [],
      };
    }

    // only fetch properties for data groups that are not cached
    this.update({dataGroups});
    groups.forEach(({groupID}) => {
      if (dataGroups[groupID].isLoading) {
        this.fetchDataGroupResourceProperties(ResourceType.User, groupID);
      }
    });
  }

  private lookupTablesLoadedCallback() {
    const tables = Object.values(lookupTablesStore.value);
    for (const table of tables) {
      const dataGroupId = table[`id`];
      const {projectId, workspaceId} = this.sourceAttrs;
      fetchLookupTableProperties(dataGroupId, projectId, workspaceId);
    }
  }

  private fetchLookupTables() {
    this._lookupTablesStoreCallback = ({fromUpdate}) => {
      if (fromUpdate) {
        // callback triggered from manual #update call (e.g. mapping registered or unregistered)
        return;
      }
      this.registerTempLookupTableMappingFromSelected();
      this.lookupTablesLoadedCallback();
    };
    lookupTablesStore.subscribe(this._lookupTablesStoreCallback);

    if (!lookupTablesStore.hasFetchedValue()) {
      lookupTablesStore.fetch({
        ...(this.attr(`project-id`) ? {projectId: this.attr(`project-id`)} : {}),
        ...(this.attr(`workspace-id`) ? {workspaceId: this.attr(`workspace-id`)} : {}),
      });
    }
  }

  private fetchUserDetails() {
    userDetailsStore.fetch();
  }

  private fetchProjectFeatureFlags() {
    // replace with project details store once it exists
    userDetailsClient
      .query({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      })
      .then((userDetails) => {
        const project = userDetails.projects[this.attr(`project-id`)];
        this.update({
          featureFlags: new Set<string>(project.feature_flags),
        });
      });
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private async fetchCohorts() {
    if (!this.attr(`project-has-cohorts`)) {
      return;
    }

    this.update({loadingCohorts: true});

    // Handle unsaved -> saved conversion on store change
    this._cohortStoreCallback = () => {
      this.update({loadingCohorts: false});

      const savedCohortItems = this.getSavedCohortSelection();
      savedCohortItems.forEach((item, index) => {
        const canonicalCohort = cohortsStore.value[item.value] as SerializedCohort;
        if (canonicalCohort && canonicalCohort.name !== item.label) {
          this.replaceItem(
            {
              resourceType: ResourceType.Cohort,
              label: canonicalCohort.name,
              value: item.value,
            },
            index,
            {rawItem: null, onItemClicked: false, autoTriggered: true, commit: false},
          );
        }
      });

      const inlineCohorts = this.getInlineCohortSelection();
      inlineCohorts.forEach(({value}, index) => {
        if (!value.cohort.raw_cohort.unsavedId) {
          return;
        }

        const unsavedCohort = cohortsStore.value[value.cohort.raw_cohort.unsavedId] as SerializedInlineCohort;

        if (!unsavedCohort) {
          return;
        }

        // Inline cohort converted
        if (Number.isInteger(unsavedCohort.id)) {
          const cohort = cohortsStore.value[unsavedCohort.id];

          this.replaceItem(
            {
              propertyDefaultType: PropertyType.List,
              resourceType: ResourceType.Cohort,
              label: cohort.name,
              value: {
                cohort,
              },
              createdBy: cohort.created_by,
            },
            index,
            {rawItem: null, onItemClicked: false, autoTriggered: true, commit: true},
          );
        } else if (!isEqual(unsavedCohort.raw_cohort, value.cohort.raw_cohort)) {
          // Inline cohort updated
          this.replaceItem(
            {
              propertyDefaultType: PropertyType.List,
              resourceType: ResourceType.Cohort,
              label: unsavedCohort.name,
              value: {
                cohort: unsavedCohort,
              },
              createdBy: unsavedCohort.created_by,
            },
            index,
            {rawItem: null, onItemClicked: false, autoTriggered: true, commit: true},
          );
        }
      });
    };

    const dataGroupId = this.attr(`data-group-id`);
    if (dataGroupId && this.attr(`support-b2b-cohorts`)) {
      cohortsStore.subscribe(this._cohortStoreCallback, dataGroupId);
      await cohortsStore.fetch(
        {
          projectId: this.attr(`project-id`),
          workspaceId: this.attr(`workspace-id`),
          dataGroupId,
        },
        {},
        dataGroupId,
      );
    } else {
      cohortsStore.subscribe(this._cohortStoreCallback);
      await cohortsStore.fetch({
        projectId: this.attr(`project-id`),
        workspaceId: this.attr(`workspace-id`),
      });
    }
  }

  private getPropertyMenuItems({
    dataGroupId,
    properties,
    resourceType,
    selected,
    propertyDefaultType,
  }: {
    dataGroupId?: string;
    properties?: any[];
    resourceType?: ResourceType;
    selected?: any;
    propertyDefaultType?: PropertyType;
  } = {}): PropertyItem[] {
    let items = properties.map((property) =>
      this.getPropertyMenuItem({dataGroupId, property, resourceType, selected, propertyDefaultType}),
    );

    const filterByTypes = this.attr(`filter-by-types`);
    if (filterByTypes) {
      items = items.filter((property) => property.isControlItem || filterByTypes.includes(property.type));
    }

    return items;
  }

  private isPropertyDisabled(property, propertyValue) {
    if (property?.customPropertyId && !isPersistedCustomProperty(property?.customPropertyId)) {
      return false;
    }

    return Boolean(
      Object.prototype.hasOwnProperty.call(this.attr(`disabled-items`), propertyValue) ||
        (!this.attr(`selectable-sensitive-properties`) && property.sensitive),
    );
  }

  // TODO opslead
  // eslint-disable-next-line sonarjs/cognitive-complexity
  private getPropertyMenuItem({
    dataGroupId,
    property,
    resourceType,
    selected,
    propertyDefaultType,
  }: {
    dataGroupId?: string;
    property?: PropertyItem;
    resourceType?: ResourceType;
    selected?: any;
    propertyDefaultType?: PropertyType;
  } = {}): PropertyItem {
    const forCohorts = resourceType === ResourceType.Cohort;
    const allowTypecast = this.attr(`allow-typecast`);
    const canTypecast = !forCohorts && allowTypecast;
    const dataDefinition = eventPropertyDefinitionsStore.value[property.name];

    if (property.resourceType) {
      // custom properties/formulas specify their own resource types
      resourceType = property.resourceType;
    }

    selected = selected || this.attr(`selected`);

    const {customPropertyId, customProperty, isControlItem, subLabelTheme} = property;
    let {label, name} = property;
    const value = property.value || name || (customProperty && customProperty.name);
    label = label || value;
    name = name || value;
    const dimensionDataGroupIdMatches =
      (selected?.dataGroupId && selected.dataGroupId === property.dimensionDataGroupId) ||
      (selected?.tempDataGroupId && selected.tempDataGroupId === property.dimensionDataGroupId);

    const isSelected =
      (forCohorts && this.isItemSelected(value, selected)) ||
      (Array.isArray(selected) && this.isItemSelected(value, selected)) ||
      (selected && dataGroupId && dataGroupId === selected.dataGroupId && selected.value === value) ||
      (dimensionDataGroupIdMatches &&
        selected.value?.[0] === property.value?.[0] &&
        selected.value?.[1] === property.value?.[1]) ||
      (selected && !selected.dataGroupId && selected.value === value && selected.resourceType === resourceType) ||
      (selected && selected.customPropertyId && selected.customPropertyId === customPropertyId) ||
      false;
    const appliedDataGroupId = property.dataGroupId ? property.dataGroupId : dataGroupId;

    const type = this.getPropertyType(name, property.type, isSelected && selected.type);
    const propertyType = propertyDefaultType || (isSelected && selected.propertyDefaultType) || property.type;
    const isDisabled = this.isPropertyDisabled(property, value);
    const tooltip = property.sensitive ? {text: DISABLED_DUE_TO_CLASSIFIED_DATA} : null;
    const isDimension = type === PropertyType.Dimension;
    return {
      ...(appliedDataGroupId ? {dataGroupId: appliedDataGroupId} : {}),
      ...(customPropertyId ? {customPropertyId} : {}),
      ...(customProperty ? {customProperty} : {}),
      ...(property.tempDataGroupId ? {tempDataGroupId: property.tempDataGroupId} : {}),
      ...(property.dimensionDataGroupId ? {dimensionDataGroupId: property.dimensionDataGroupId} : {}),
      ...(forCohorts ? property : {}),
      ...(property.items ? {items: property.items} : {}),
      ...(property.tooltipData ? {tooltipData: property.tooltipData} : {}),
      hasCaret: (isDimension || this.attr(`show-items-caret`)) && !isControlItem && !property.sensitive,
      hasPill: !isDimension && canTypecast && !isControlItem && !property.sensitive,
      hidden: !!property.hidden,
      icon: property.icon || getIconForPropertyType(type),
      isControlItem,
      isDisabled,
      tooltip,
      isSelected,
      joinPropertyType: property.joinPropertyType,
      label,
      name,
      pillAction: PropertyItemAction.Typecast,
      pillLabel: `Typecast`,
      propertyDefaultType: propertyType,
      resourceType,
      type,
      value,
      ...(property.joinPropertyLabel ? {subLabel: property.joinPropertyLabel} : {}),
      isLookupTableProperty: property.isLookupTableProperty,
      ...(subLabelTheme ? {subLabelTheme} : {}),
      searchableContent: [dataDefinition?.description, dataDefinition?.name].join(` `),
    };
  }

  private getPropertyType(name: string, type: PropertyType, selectedType?: PropertyType): PropertyType {
    if (selectedType && type !== PropertyType.Dimension) {
      return selectedType;
    }
    return STRING_TYPECAST_PROPERTIES.includes(name) ? PropertyType.String : type;
  }

  private getArraySelection() {
    const selected = this.attr(`selected`);

    if (Array.isArray(selected)) {
      return selected;
    }

    return [];
  }

  private isDimensionSelected(selected) {
    if (!selected) {
      return false;
    }
    // lookup table (aka dimension property) selected.value is an array of two strings (vs b2b profiles)
    return (selected.dataGroupId || selected.tempDataGroupId) && Array.isArray(selected.value);
  }

  private isItemSelected(value, selection: PropertyItem[] | PropertyItem) {
    const getCompareValue = (v) => (v?.cohort?.raw_cohort ? v.cohort.raw_cohort.unsavedId : v);

    if (Array.isArray(selection)) {
      return selection.some((item) => getCompareValue(item.value) === getCompareValue(value));
    }

    return selection && getCompareValue(selection.value) === getCompareValue(value);
  }

  private async editCohort(item) {
    const cohortParsed = item.id ? cohortsStore.value[item.id] : item.value?.cohort?.raw_cohort || item.value?.cohort;
    const cohort = CohortModel.parseCohort(cohortParsed);
    const {cohortStagedValue, cohortSavedValue} = await this.promptInlineCohortsModal(cohort);

    if (cohortStagedValue?.cohort || cohortSavedValue?.cohort) {
      this.handleCohortUpdate(cohortStagedValue, cohortSavedValue);
      this.trackEvent(TrackingEvent.OpenInlineCohortsModal, {
        action: `Edit`,
        context: this._context,
      });
    }
  }

  /**
   * Adding a temporary cohort, editing a temporary cohort, saving a new cohort directly,
   * or saving a temporary cohort
   * @param {object} [options]
   * @param {number} [options.id] - id of the cohort, only applicable if saving the cohort
   * @param {CohortModel} [options.cohort] - cohort being passed in, only exists if not saving the cohort
   * @param {string} [options.name] - name of the cohort
   * @param {boolean} [options.savedAsNew] - whether we are saving the cohort as new
   *
   * Adding a new temporary cohort - name, cohort
   * []Editing a temporary cohort - name, cohort
   * []Saving an existing temporary cohort - id, name, cohort
   * Saving a new cohort / editing an existing cohort - id, name, cohort, savedAsNew = false
   * Saving an existing cohort as new - id, name, cohort, savedAsNew = true
   */
  private commitCohort({id, cohort, name, savedAsNew}: InlineCohortsModalCommitEvent['detail']) {
    let itemCohort: SerializedCohort | SerializedInlineCohort;
    const isSaved = Boolean(id);
    const isEditingTemporaryCohort = Boolean(cohort.unsavedId);
    /**
     * cohort.unsavedId
     * "" - saving a new cohort
     * null - updating or saving an existing cohort as new
     * a string - editing or saving an existing temporary cohort
     */
    const unsavedId = cohort.unsavedId || cuid();

    // applies to an existing and temporary cohort, both are considered saved states
    if (isSaved) {
      const item = this.getCohortPropertyItem(name, this.getCreatedBy(), id);
      this.replaceOrSelectCohort(item, isEditingTemporaryCohort || isSaved, unsavedId);
    } else {
      itemCohort = {
        name,
        raw_cohort: {
          unsavedId,
          ...pick(cohort.serialize(), [`name`, `behaviors`, `selector`, `data_group_id`]),
          ...(userDetailsStore.value
            ? {
                createdBy: {
                  id: this.attr(`user-id`),
                  name: userDetailsStore.value.name,
                  email: userDetailsStore.value.email,
                },
              }
            : {}),
        },
      } as SerializedInlineCohort;
      const item = this.getCohortPropertyItem(name, this.getCreatedBy(), {cohort: itemCohort});
      this.replaceOrSelectCohort(item, isEditingTemporaryCohort, unsavedId);
    }

    if (!isSaved) {
      // updating cohort store for cohorts that are temporary
      const unsavedCohort = itemCohort as SerializedInlineCohort;
      cohortsStore.update({
        [unsavedId]: {
          name,
          raw_cohort: unsavedCohort.raw_cohort,
          created_by: cohort.createdBy,
        } as SerializedInlineCohort,
      });
    } else if (isEditingTemporaryCohort && isSaved) {
      // Mark unsaved cohort being converted to a saved cohort
      cohortsStore.update({
        [unsavedId]: {
          name,
          created_by: cohort.createdBy,
          ...(cohortsStore.value[unsavedId] || {}),
          id: cohort.id,
        } as SerializedInlineCohort,
      });
    }
    const isEditingExistingCohort = cohort.unsavedId === null && savedAsNew;
    this.trackInlineCohorts(name, isSaved, isEditingTemporaryCohort, isEditingExistingCohort, savedAsNew);
  }

  private trackInlineCohorts(name, isSaved, isEditingTemporaryCohort, isEditingExistingCohort, savedAsNew) {
    if (isSaved) {
      this.trackEvent(TrackingEvent.SaveInlineCohorts, {
        context: this._context,
        name,
        // Indicates whether an applied inline cohort was later saved
        converted: !!isEditingTemporaryCohort,
        savedAsNew,
      });
    } else if (!isEditingTemporaryCohort) {
      this.trackEvent(TrackingEvent.CreateInlineCohort, {
        context: this._context,
        name,
      });
    } else {
      this.trackEvent(TrackingEvent.EditInlineCohorts, {
        context: this._context,
        name,
        existing: isEditingExistingCohort,
      });
    }
  }

  // add cohort to the selected attr, either replace an existing selected cohort, or add a new cohort
  // to the selected properties menu
  private replaceOrSelectCohort(item: PropertyItem, isEditingCohort: boolean, unsavedId: string) {
    const selected = this.attr(`selected`);

    // based on internal user feedback cohorts created inline
    // should be committed much like custom props with breakdowns
    const index = Array.isArray(selected)
      ? this.getArraySelection().findIndex(
          (selectedItem) =>
            selectedItem.value.cohort?.raw_cohort?.unsavedId === unsavedId || selectedItem.value === item.value,
        )
      : isEqual(selected?.value, item.value);

    if (isEditingCohort && selected && index > -1) {
      this.replaceItem(item, index, {rawItem: null, onItemClicked: false, commit: true});
    } else {
      this.selectItem(item, {rawItem: null, onItemClicked: false, commit: true});
    }
  }

  private getCreatedBy(): User {
    return {
      id: this.attr(`user-id`),
      name: userDetailsStore?.value.name,
      email: userDetailsStore?.value.email,
    };
  }

  private getCohortPropertyItem(name: string, createdBy: User, value: TODO) {
    return {
      type: PropertyType.List,
      propertyDefaultType: PropertyType.List,
      resourceType: ResourceType.Cohort,
      label: name,
      value,
      createdBy,
    } as PropertyItem;
  }

  private getSavedCohortSelection(): SavedCohortItem[] {
    const selected = this.attr(`selected`);
    const cohorts = Array.isArray(selected) ? selected : [selected];

    return cohorts.filter((item) => item?.resourceType === ResourceType.Cohort && Number.isInteger(item?.value));
  }

  private getInlineCohortSelection() {
    const selected = this.attr(`selected`);
    const cohorts = Array.isArray(selected) ? selected : [selected];

    return cohorts.filter((item) => item && item.value && item.value.cohort && item.value.cohort.raw_cohort);
  }

  private getInlineCohortItems() {
    return this.getInlineCohortSelection().map((item) => ({
      ...item,
      createdBy: item.value.cohort.raw_cohort.createdBy,
      resourceType: ResourceType.Cohort,
    }));
  }

  private getSelectedCustomFormula(resourceType) {
    const selected = this.attr(`selected`) ? [this.attr(`selected`)] : [];

    return selected.filter(
      (item) =>
        item.resourceType === resourceType &&
        item.customPropertyId &&
        !isPersistedCustomProperty(item.customPropertyId),
    );
  }

  private registerTempLookupTableMappingFromSelected() {
    const selected = this.attr(`selected`);
    const tempDataGroupId = selected?.tempDataGroupId;
    if (tempDataGroupId) {
      const propertyName = Array.isArray(selected.value) ? selected.value[0] : selected.value;
      registerTempLookupTableMapping(this.getSelectedResourceType(), propertyName, tempDataGroupId);
    }
  }

  private addTempMappingsToProperties(resourceType: ResourceType.Event | ResourceType.User, storeProperties) {
    const mappings = getTempLookupTableMappings(resourceType);
    return storeProperties.map((storeProperty) => {
      // storeProperty is of type PropertyItem ('name' field used, 'value' field not used for name),
      // but the store is typed as Record<string,any>
      if (mappings.has(storeProperty.name)) {
        const tempDataGroupId = mappings.get(storeProperty.name);
        const joinPropertyType = storeProperty.joinPropertyType || storeProperty.type;
        return {...storeProperty, tempDataGroupId, type: PropertyType.Dimension, joinPropertyType};
      } else {
        return storeProperty;
      }
    });
  }

  set useMultipleSelection(value) {
    this._useMultipleSelection = value;
    this.update();
  }

  set context(value) {
    this._context = value;
  }

  set getSortedMenuSections(getSortedMenuSections) {
    this.update({getSortedMenuSections});
  }

  dispatchClosedModal() {
    this.dispatchEvent(new CustomEvent(`closedModal`));
  }
}

export const PropertiesMenuEl = defineAndMakeJsxFactory(`mp-properties-menu`, PropertiesMenu);
